/**
 * Map of message keys, mapping the full name of each key to its encoded form.
 *
 * **All keys used in a message must also be defined here.**
 */
export enum MessageKey {
  type = 't',
  data = 'd',
  recipient = 'rc',
  response = 'rs',
  request = 'rq',
  origin = 'og',
  agentOwnerAccountId = 'aai',
  agentOperationId = 'aoi',
  agentOperationUpdateIndex = 'aox',
  agentOperationUpdateCount = 'aoc',
  currentYjsClock = 'cyc',
  previousYjsClock = 'pyc',
  transientTransactionData = 'ttd',
  telepointerData = 'tpd',
  cameraData = 'cd',
  typingData = 'td',
  racingData = 'rd',
  slidesData = 'sld',
  participantData = 'pd',
  selectionData = 'sed',
  smartLinksUpdateData = 'sud',
  timestamp = 'ts',
  latestStep = 'ls',
  sync = 'sy',
  messages = 'm',
  encodingTypes = 'et',
}

export enum MessageType {
  /**
   * A message broadcast from one client (via server-side socket) -> all
   * clients, indicating a change in content (via changeset) on a piece of
   * content.
   */
  Update = 0x1,
  /**
   * A client-to-server message used to initiate a sync phase.
   */
  SyncInit = 0x2,
  /**
   * A server-to-client message used during the sync phase.
   */
  SyncPhase0 = 0x3,
  /**
   * A client-to-client message used during the sync phase, which originates
   * from a client, and is relayed from server -> a specific recipient.
   */
  SyncPhase1 = 0x4,
  /**
   * A client-to-client message used during the sync phase, which originates
   * from a client, and is relayed from server -> a specific recipient.
   */
  SyncPhase2 = 0x5,
  /**
   * A message broadcast from one client (via server-side socket) -> all
   * clients, for sharing transient information (user info, transient updates)
   */
  Awareness = 0x6,
  /**
   * A message broadcast from server -> all clients to ask them to take an action.
   */
  Command = 0x7,
  LargeUpdate = 0x8,
  /**
   * A server-to-client message broadcast to a specific recipient upon connection to the socket.
   */
  SessionStatus = 0x9,
  /**
   * A batched update contains many other changes rolled up into a single message
   */
  BatchedUpdate = 0xa,
}

export type MessageTypeValue = ParseInt<`${MessageType}`>;

export const messageTypeValues: MessageTypeValue[] = Object.values(MessageType).filter(
  (x): x is MessageTypeValue => typeof x === 'number',
);

/**
 * Convert a message type to a string for debugging purposes
 * Note: do not use this in production code, as it is not performant.
 * @param type
 */
export function messageTypeToString(type: MessageType): string {
  return (
    Object.entries(MessageType).find(([key, value]) => {
      if (value === type) {
        return key;
      }
    })?.[0] ?? ''
  );
}

export type ParseInt<T extends `${number}`> = T extends any
  ? T extends `${infer Digit extends number}`
    ? Digit
    : never
  : never;

type AllKeys<T> = T extends any ? keyof T : never;

type CommonKeys<T extends object> = keyof T;

type Subtract<A, C> = A extends C ? never : A;

type NonCommonKeys<T extends object> = Subtract<AllKeys<T>, CommonKeys<T>>;

type PickType<T, K extends AllKeys<T>> = T extends { [k in K]?: any } ? T[K] : undefined;

type PickTypeOf<T, K extends string | number | symbol> =
  K extends AllKeys<T> ? PickType<T, K> : never;

export type Merge<T extends object> = {
  [k in CommonKeys<T>]: PickTypeOf<T, k>;
} & {
  [k in NonCommonKeys<T>]?: PickTypeOf<T, k>;
};

/**
 * Union type of all message keys in their full name form.
 */
export type FullMessageKey = keyof typeof MessageKey;

/**
 * Union type of all message keys in their encoded form.
 */
export type EncodedMessageKey = `${MessageKey}`;

type ValidKeys = {
  [Key in FullMessageKey]: any;
};

/**
 * Defines the encoding type for awareness messages. These values are sent
 * in the message metadata to indicate how a message should be decoded. The
 * enum uses hex values to be more compact.
 */
export enum AwarenessEncodingType {
  String = 0x0,
  Binary = 0x1,
}

export const awarenessEncodingTypeValues: AwarenessEncodingType[] = Object.values(
  AwarenessEncodingType,
).filter((x): x is AwarenessEncodingType => typeof x === 'number');

export interface MessageMetadata<
  Type extends MessageType,
  Data extends Record<string, any>,
> {
  type: Type;
  data: Data;
  // Optional encoding type information for awareness messages
  // Indicates whether each data field was encoded using binary or string format
  encodingTypes?: Type extends MessageType.Awareness
    ? {
        [MessageKey.transientTransactionData]?: AwarenessEncodingType;
        [MessageKey.telepointerData]?: AwarenessEncodingType;
        [MessageKey.participantData]?: AwarenessEncodingType;
        [MessageKey.selectionData]?: AwarenessEncodingType;
        [MessageKey.smartLinksUpdateData]?: AwarenessEncodingType;
        [MessageKey.cameraData]?: AwarenessEncodingType;
        [MessageKey.typingData]?: AwarenessEncodingType;
        [MessageKey.racingData]?: AwarenessEncodingType;
        [MessageKey.slidesData]?: AwarenessEncodingType;
      }
    : never;
}

/**
 * Helper type that does the following:
 *  - Ensures every property in `data` is defined in the {@link MessageKey} enum.
 *  - Adds `type` property.
 */
export type BaseMessage<
  Type extends MessageType,
  Data extends Record<string, any>,
> = ValidKeys extends Data
  ? ValidKeys extends MessageMetadata<Type, Data>
    ? MessageMetadata<Type, Data>
    : never
  : never;

export enum UpdateMessageOrigin {
  // 0th bit
  None = 0,
  UndoRedo = 1,
  // Now involving 1st bit
  CleanDocument = 2,
  Resizing = 3,
  Agent = 4,
  LayoutShift = 5,
  BackendAgent = 6,
}

export const originBitRange = Object.values(UpdateMessageOrigin).reduce(
  (current1Bits, newValue) =>
    typeof newValue === 'number' ? current1Bits | newValue : current1Bits,
  0,
);

export type AgentOperationUpdateProgress = {
  /**
   * Zero-based index of this Yjs update within the backend agent operation.
   * This includes updates that add no elements, such as constraint-solving
   * transactions that only reposition existing elements.
   */
  agentOperationUpdateIndex: number;
  /**
   * Total number of Yjs updates in the backend agent operation. Present only
   * on the update whose {@link agentOperationUpdateIndex} is zero.
   */
  agentOperationUpdateCount?: number;
};

// TODO: Make agentOperationId required after backend-agent operation IDs are
// fully deployed. Make agentOperationUpdateIndex required once the single-step
// OfflineDocument.save() and OfflineDocumentV2.save() paths emit index 0/count 1.
type BackendAgentUpdateData = {
  origin: UpdateMessageOrigin.BackendAgent;
  /**
   * Account ID of the user on whose behalf the backend agent performed the operation.
   */
  agentOwnerAccountId?: string;
  /**
   * Opaque identifier shared by updates from the same logical backend agent operation.
   */
  agentOperationId?: string;
  currentYjsClock?: number | null;
  previousYjsClock?: number | null;
  data: Uint8Array;
} & Partial<AgentOperationUpdateProgress>;

type UpdateData = {
  origin: Exclude<UpdateMessageOrigin, UpdateMessageOrigin.BackendAgent>;
  currentYjsClock?: number | null;
  previousYjsClock?: number | null;
  data: Uint8Array;
};

export type UpdateMessage = BaseMessage<
  MessageType.Update,
  BackendAgentUpdateData | UpdateData
>;

export type LargeStepsSignedUrl = {
  presignedUrl: string;
  accessToken?: string;
};

export type LargeUpdateMessage = BaseMessage<
  MessageType.LargeUpdate,
  {
    // data will be an S3 URL string or OS pre-signed download URL object when a large update is being broadcast
    data: string | LargeStepsSignedUrl;
  }
>;

export type BatchedUpdateMessage = BaseMessage<
  MessageType.BatchedUpdate,
  {
    messages: (UpdateMessage | LargeUpdateMessage)[];
    sync?: boolean;
  }
>;
export type SessionStatusMessage = BaseMessage<
  MessageType.SessionStatus,
  {
    timestamp: number;
    latestStep: string;
  }
>;

export type SyncInitMessage = BaseMessage<
  MessageType.SyncInit,
  {
    data: Uint8Array;
  }
>;

export type SyncPhase0Message = BaseMessage<
  MessageType.SyncPhase0,
  {
    recipient: string;
    request: Uint8Array;
  }
>;

export type SyncPhase1Message = BaseMessage<
  MessageType.SyncPhase1,
  {
    recipient: string;
    response: Uint8Array;
    request: Uint8Array;
  }
>;

export type SyncPhase2Message = BaseMessage<
  MessageType.SyncPhase2,
  {
    recipient: string;
    response: Uint8Array;
  }
>;

export type AwarenessMessage = BaseMessage<
  MessageType.Awareness,
  {
    /**
     * Used for transient transaction data (e.g. dragging), which modify the board over
     * awareness until the transaction has ended at which point the data is persisted.
     * May be omitted to not update.
     */
    transientTransactionData?: Uint8Array | null;
    /**
     * Future stream which may be used for telepointer awareness.
     */
    telepointerData?: Uint8Array | null;
    /**
     * Future stream which may be used for telepointer awareness.
     */
    participantData?: Uint8Array | null;
    /**
     * Future stream which may be used for selection awareness.
     */
    selectionData?: Uint8Array | null;
    /**
     * Used for smart link awareness for clients refreshing smart links
     * and inducing a refresh on other clients
     */
    smartLinksUpdateData?: Uint8Array | null;
    cameraData?: Uint8Array | null;
    /**
     * Future stream which may be used for typing awareness.
     */
    typingData?: Uint8Array | null;
    /**
     * Used for racing lap times awareness.
     */
    racingData?: Uint8Array | null;
    /**
     * Used for slides presence: which slide each collaborator is viewing.
     */
    slidesData?: Uint8Array | null;
  }
>;

export type CommandMessage = BaseMessage<
  MessageType.Command,
  {
    data: Uint8Array;
  }
>;

/**
 * A union of all message types.
 */
export type Message =
  | UpdateMessage
  | LargeUpdateMessage
  | SyncInitMessage
  | SyncPhase0Message
  | SyncPhase1Message
  | SyncPhase2Message
  | AwarenessMessage
  | CommandMessage
  | SessionStatusMessage
  | BatchedUpdateMessage;

/**
 * A single type that contains every property from all message types.
 */
export type AnyMessage = {
  [K in keyof Message]: Message[K] extends Record<string, any>
    ? Merge<Message[K]>
    : Message[K];
};
